// Globalny store stanu instalacji PWA.
//
// Zdarzenie `beforeinstallprompt` (Android/Chrome) potrafi wystrzelić bardzo wcześnie —
// zanim jakikolwiek komponent React zdąży się zamontować. Dlatego nasłuch rejestrujemy
// raz, na poziomie modułu (import w index.tsx), i zapamiętujemy odroczone zdarzenie tutaj.
// Komponenty czytają ten stan przez hook usePwaInstall (useSyncExternalStore).

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = detectInstalled();

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches;
  // iOS Safari nie wspiera display-mode — używa własnej flagi navigator.standalone.
  const iosStandalone = (window.navigator as any).standalone === true;
  return Boolean(standalone || iosStandalone);
}

export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIphoneIpad = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ podaje się za Maca — wykrywamy po dotyku.
  const isIpadOS =
    window.navigator.platform === 'MacIntel' &&
    window.navigator.maxTouchPoints > 1;
  return isIphoneIpad || isIpadOS;
}

export function isAndroid(): boolean {
  if (typeof window === 'undefined') return false;
  return /Android/.test(window.navigator.userAgent);
}

// Na iOS dodać do ekranu głównego można TYLKO z Safari (nie z Chrome/FB/in-app).
export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram/.test(ua);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // blokujemy domyślny mini-pasek, pokażemy własny dymek
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    emit();
  });
}

export const pwaInstall = {
  subscribe(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  },
  getDeferred(): BeforeInstallPromptEvent | null {
    return deferredPrompt;
  },
  isInstalled(): boolean {
    return installed;
  },
  // Uruchamia natywny prompt instalacji (Android/Chrome). Zwraca wynik wyboru użytkownika
  // albo 'unavailable', gdy prompt nie jest dostępny (np. iOS).
  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!deferredPrompt) return 'unavailable';
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // Zdarzenie można zużyć tylko raz.
    deferredPrompt = null;
    emit();
    return outcome;
  },
};
